import { guardNever } from "@hex/common";
import { useCallback } from "react";

import {
  SessionStorageKeys,
  useSessionStorageSetter,
} from "../../../../hooks/useSessionStorage";
import { useDispatch } from "../../../../redux/hooks";
import {
  DATA_BROWSER_EMPTY_SELECTION,
  setDataBrowserRecommendedView,
} from "../../../../redux/slices/dataBrowserSlice";
import {
  DataBrowserRecommendedViewPanel,
  DataBrowserRecommendedViewSelection,
} from "../utils/recommendedView";

import { UseEndorsedDataItem } from "./useEndorsedData";
import { UseFavoritesDataItem } from "./useFavoritesData";
import { UseRecentlyUsedDataItem } from "./useRecentlyUsedData";

type Item =
  | UseEndorsedDataItem
  | UseFavoritesDataItem
  | UseRecentlyUsedDataItem;

export type UseDataBrowserRecommendedViewResult = {
  /**
   * Navigate to the given panel under the Recommended tab view. Optionally,
   * with the given selection.
   */
  navigateToPanel: (
    panel: DataBrowserRecommendedViewPanel,
    selection?: DataBrowserRecommendedViewSelection,
  ) => void;
  /**
   * Navigate to the given panel and the selection that corresponds to the given
   * item. An item is a piece of normalized data that appears in the recommended
   * view.
   */
  navigateToItem: (item: Item, panel: DataBrowserRecommendedViewPanel) => void;
  /**
   * Set the selection in the current Recommended view. An item is a piece of
   * normalized data that appears in the recommended view.
   */
  selectItem: (item: Item) => void;
};

/**
 * A hook for navigating to and selecting items in the Recommended view of the Data Browser.
 */
export function useDataBrowserRecommendedView(): UseDataBrowserRecommendedViewResult {
  const dispatch = useDispatch();

  // redux store state does not persist across page reloads, so we need to use session
  // storage and keep them in sync
  const setSessionStorage = useSessionStorageSetter(
    SessionStorageKeys.DATA_BROWSER_RECOMMENDED_View(),
  );

  const navigateToPanel = useCallback<
    UseDataBrowserRecommendedViewResult["navigateToPanel"]
  >(
    (panel, selection = DATA_BROWSER_EMPTY_SELECTION) => {
      if (panel === DataBrowserRecommendedViewPanel.OVERVIEW) {
        dispatch(setDataBrowserRecommendedView({ panel, selection }));
        setSessionStorage({ panel, selection });
      } else {
        dispatch(setDataBrowserRecommendedView({ panel }));
        setSessionStorage((prev) => ({ ...prev, panel }));
      }
    },
    [dispatch, setSessionStorage],
  );

  const navigateToItem = useCallback<
    UseDataBrowserRecommendedViewResult["navigateToItem"]
  >(
    (item, panel) => {
      const selection = recommendedDataItemToSelection(item);
      dispatch(setDataBrowserRecommendedView({ panel, selection }));
      setSessionStorage({ panel, selection });
    },
    [dispatch, setSessionStorage],
  );

  const selectItem = useCallback<
    UseDataBrowserRecommendedViewResult["selectItem"]
  >(
    (item) => {
      const selection = recommendedDataItemToSelection(item);
      dispatch(setDataBrowserRecommendedView({ selection }));
      setSessionStorage((prev) => ({ ...prev, selection }));
    },
    [dispatch, setSessionStorage],
  );

  return {
    navigateToItem,
    navigateToPanel,
    selectItem,
  };
}

/**
 * Translate an item (normalized data that appears in the recommended view) to a
 * selection for the Recommended view.
 */
export function recommendedDataItemToSelection<T extends Item>(
  item: T,
): DataBrowserRecommendedViewSelection {
  if (item.data.__typename === "SemanticDataset") {
    const dataConnectionId = item.data.semanticProject.dataConnection?.id;
    if (dataConnectionId == null) {
      throw new Error("Semantic dataset has no data connection");
    }
    return {
      displayObjectType: "semanticDataset",
      dataBrowserSemanticDatasetId:
        "semanticDatasetId" in item.data
          ? item.data.semanticDatasetId
          : item.data.id,
      dataBrowserDataConnectionId: dataConnectionId,
      dataBrowserTableId: null,
      dataBrowserSchemaId: null,
      dataBrowserSemanticViewId: null,
    };
  } else if (item.data.__typename === "DataSourceTable") {
    return {
      displayObjectType: "table",
      dataBrowserTableId: item.data.id,
      dataBrowserDataConnectionId: item.data.dataConnectionId,
      dataBrowserSemanticDatasetId: null,
      dataBrowserSemanticViewId: null,
      dataBrowserSchemaId: null,
    };
  } else if (item.data.__typename === "DataSourceSchema") {
    return {
      displayObjectType: "schema",
      dataBrowserSchemaId: item.data.id,
      dataBrowserDataConnectionId: item.data.dataConnectionId,
      dataBrowserSemanticDatasetId: null,
      dataBrowserSemanticViewId: null,
      dataBrowserTableId: null,
    };
  } else if (item.data.__typename === "SemanticView") {
    const dataConnectionId = item.data.semanticProject.dataConnection?.id;
    if (dataConnectionId == null) {
      throw new Error("Semantic view has no data connection");
    }
    return {
      displayObjectType: "semanticView",
      dataBrowserDataConnectionId: dataConnectionId,
      dataBrowserSchemaId: null,
      dataBrowserSemanticDatasetId: null,
      dataBrowserSemanticViewId: item.data.id,
      dataBrowserTableId: null,
    };
  } else {
    guardNever(item.data, item);
    return DATA_BROWSER_EMPTY_SELECTION;
  }
}
